import type { Board } from 'chessboard/model/board/types';
import type { GameAPI } from 'chessboard/types';
import type { GetSet } from 'src/utils/create-get-set';
import type { Modes } from 'modes/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import type { Queue } from 'utils/queue/config/types';
import type { Renderer } from 'chessboard/renderers/config/types';

export namespace Core {
  export interface API {
    getContext: () => Context;
    getMode: () => Modes.API.Extended;
    run: (event: Core.Event) => void;
    setMode: (mode: Modes.API.Extended) => void;
    setRenderer: (renderer?: Renderer.Model) => void;
  }

  export interface Context extends ContextParams {
    queue: Queue.API;
    renderer?: Renderer.Model;
    reset: () => void;
    run: Run;
    setMode: (mode: Modes.API.Extended) => void;
    setRenderer: (renderer?: Renderer.Model) => void;
    state: GetSet<State>;
  }

  export interface ContextParams {
    api: GameAPI;
    el: Board.Element;
    mode: Modes.API.Extended<{ canDropPiecesContinuously?: Modes.Options.Initial.Option }>;
    options: Board.Options;
  }

  export interface CreateParams {
    plugins: Plugins.API;
    params: any;
  }

  export enum LogCodes {
    Event = 'Event',
    State = 'State',
  }

  export enum State {
    DraggingPiece = 'draggingPiece',
    HideHoverSquare = 'hideHoverSquare',
    MachineState = 'machineState',
    PromotionMove = 'promotionMove',
    SoundPlayed = 'soundPlayed',
    SelectedPiece = 'selectedPiece',
  }

  export type Run = (event: Event) => void;

  export type EventType =
    | APIEvents
    | BoardEvents
    | InstanceEvents
    | OptionsEvents
    | SoundEvents
    | UIEvents
    | UserEvents;

  export interface Event<T = any, U = any> {
    data: U;
    type: T;
    [key: string]: any;
  }

  export enum APIEvents {
    AddMove = 'AddMove',
    BlinkHighlight = 'BlinkHighlight',
    ClearMarkings = 'ClearMarkings',
    CreateContinuation = 'CreateContinuation',
    CreateGame = 'CreateGame',
    DeletePosition = 'DeletePosition',
    DrawAgreed = 'DrawAgreed',
    DrawClaimed = 'DrawClaimed',
    GameResigned = 'GameResigned',
    IllegalMove = 'IllegalMove',
    LineCommentUpdated = 'LineCommentUpdated',
    LineUpdated = 'LineUpdated',
    Load = 'Load',
    Mark = 'Mark',
    Move = 'Move',
    MoveBackward = 'MoveBackward',
    MoveForward = 'MoveForward',
    MoveVariation = 'MoveVariation',
    NodeUpdated = 'NodeUpdated',
    NodeLimitsUpdated = 'NodeLimitsUpdated',
    OutOfTime = 'OutOfTime',
    PromoteVariation = 'PromoteVariation',
    Reload = 'Reload',
    ResetGame = 'ResetGame',
    ResetToMainLine = 'ResetToMainLine',
    SelectLineEnd = 'SelectLineEnd',
    SelectNode = 'SelectNode',
    SelectLineStart = 'SelectLineStart',
    SetBoardPosition = 'SetBoardPosition',
    SetPlayingAs = 'SetPlayingAs',
    TurnSet = 'TurnSet',
    Undo = 'Undo',
    Unmark = 'Unmark',
    UpdatePGNHeaders = 'UpdatePGNHeaders',
  }

  export enum BoardEvents {
    PromotionAreaClosePointerdown = 'PromotionAreaClosePointerdown',
    PromotionPiecePointerdown = 'PromotionPiecePointerdown',
  }

  export enum InstanceEvents {
    Create = 'Create',
    Destroy = 'Destroy',
    ModeChanged = 'ModeChanged',
    RendererSet = 'RendererSet',
    RendererSetFailed = 'RendererSetFailed',
    WebGLAssetsInitialized = 'WebGLAssetsInitialized',
    StylesSetFailed = 'StylesSetFailed',
  }

  export enum OptionsEvents {
    Update = 'UpdateOptions',
    UpdateMode = 'UpdateModeOptions',
  }

  export enum SoundEvents {
    Play = 'PlaySound',
  }

  export enum UIEvents {
    Resize = 'Resize',
    ToggleHotkeyLegend = 'ToggleHotkeyLegend',
  }

  export enum UserEvents {
    MousedownLeft = 'MousedownLeft',
    MousedownRight = 'MousedownRight',
    PointerdownLeft = 'PointerdownLeft',
    PointerdownRight = 'PointerdownRight',
    Pointermove = 'Pointermove',
    Pointerup = 'Pointerup',
    Wheel = 'Wheel',
  }

  export enum FairPlay {
    UntrustedUserEvent = 'UntrustedUserEvent',
  }
}
